import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, addDoc, updateDoc, doc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// 最終ログインからこの日数を過ぎた会社を「休眠」として警告する
const DORMANT_DAYS = 183;   // およそ6か月

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", adminEmail: "", plan: "free", maxUsers: 3 });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);
  const [editing, setEditing] = useState(null);   // プランを変更中の会社
  const [busy, setBusy] = useState(false);
  const [lastLogins, setLastLogins] = useState({});   // 会社コード → 最終ログイン
  const [deleting, setDeleting] = useState(null);     // 削除確認中の会社
  const [confirmCode, setConfirmCode] = useState("");
  const [preview, setPreview] = useState(null);       // 削除される件数
  const [onlyDormant, setOnlyDormant] = useState(false);

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    const snap = await getDocs(collection(db, "companies"));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => b.createdAt - a.createdAt);
    setCompanies(data);

    // 会社ごとの最終ログイン（所属ユーザーの中でいちばん新しいもの）
    try {
      const uSnap = await getDocs(collection(db, "users"));
      const map = {};
      uSnap.docs.forEach(d => {
        const u = d.data();
        if (!u.companyId || !u.lastLoginAt?.toDate) return;
        const at = u.lastLoginAt.toDate();
        if (!map[u.companyId] || at > map[u.companyId]) map[u.companyId] = at;
      });
      setLastLogins(map);
    } catch (e) {
      console.warn("最終ログインの取得に失敗しました:", e);
    }
  };

  const daysSince = (d) => d ? Math.floor((Date.now() - d.getTime()) / 86400000) : null;
  const LABELS = {
    users: "社員", authAccounts: "ログイン用アカウント",
    attendance: "勤怠", kioskUsers: "スマホ打刻の登録", kioskAttendance: "旧打刻",
    devices: "打刻端末", notices: "お知らせ", documents: "書類申請", companyDocs: "ライブラリー",
    payslips: "給与明細(Excel)", payrollProfiles: "給与情報", payrolls: "給与計算",
    shifts: "シフト表", inquiries: "お問い合わせ", payslipTemplates: "明細テンプレート",
    payrollSettings: "給与設定", companyCalendars: "会社カレンダー", notifySettings: "通知設定",
  };
  const isDormant = (c) => {
    const last = lastLogins[c.id];
    const base = last || c.createdAt?.toDate?.();
    const n = daysSince(base);
    return n !== null && n >= DORMANT_DAYS;
  };

  // 削除する前に、消える件数を数えて見せる
  const openDelete = async (c) => {
    setDeleting(c); setConfirmCode(""); setPreview(null);
    try {
      const call = httpsCallable(getFunctions(undefined, "us-central1"), "deleteCompany");
      const res = await call({ companyId: c.id, dryRun: true });
      setPreview(res.data.counts || {});
    } catch (e) {
      console.error("削除対象の確認に失敗しました:", e);
      setPreview({});
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const call = httpsCallable(getFunctions(undefined, "us-central1"), "deleteCompany");
      const res = await call({ companyId: deleting.id, confirmCode });
      setDeleting(null);
      await fetchCompanies();
      alert(res.data?.authError
        ? `会社データは削除しました。\nただしログイン用アカウントを削除できませんでした：${res.data.authError}\nFirebaseコンソールのAuthenticationから手動で削除してください。`
        : "削除しました。");
    } catch (e) {
      console.error("会社の削除に失敗しました:", e);
      alert(`削除に失敗しました。${e?.message || ""}`);
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const companyId = form.name.replace(/\s+/g, "-").toLowerCase() + "-" + Date.now();
    await addDoc(collection(db, "companies"), {
      ...form,
      companyId,
      status: "active",
      maxUsers: Number(form.maxUsers),
      createdAt: Timestamp.now(),
    });
    setForm({ name: "", adminEmail: "", plan: "free", maxUsers: 3 });
    setShowForm(false);
    fetchCompanies();
    setSubmitting(false);
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === "active" ? "suspended" : "active";
    await updateDoc(doc(db, "companies", c.id), { status: newStatus });
    fetchCompanies();
  };

  // ロゴをbase64に変換してFirestoreに保存
  const handleLogoUpload = async (companyDocId, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert("ロゴは500KB以下の画像を選択してください");
      return;
    }
    setUploadingLogoFor(companyDocId);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      await updateDoc(doc(db, "companies", companyDocId), { logo: base64 });
      fetchCompanies();
      setUploadingLogoFor(null);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDelete = async (companyDocId) => {
    await updateDoc(doc(db, "companies", companyDocId), { logo: "" });
    fetchCompanies();
  };

  const fmt = (ts) => ts?.toDate?.().toLocaleDateString("ja-JP") || "";
  const fmtDT = (ts) => ts?.toDate?.().toLocaleString("ja-JP", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }) || "—";

  const PLAN_LABELS = {
    trial: "トライアル（無料）",
    free: "フリー（無料）",
    small: "スモール（500円/月）",
    standard: "スタンダード（1,500円/月）",
  };

  const BILLING_LABELS = {
    trial:        { text: "トライアル", color: "#2980b9" },
    unregistered: { text: "カード未登録", color: "#e67e22" },
    active:       { text: "決済中",      color: "#27ae60" },
    past_due:     { text: "支払い失敗",  color: "#e74c3c" },
    canceled:     { text: "解約済み",    color: "#95a5a6" },
  };

  const SIGNUP_URL = "https://miyabicompany-manager.web.app/?signup=1";

  // 運営がプラン・決済状況・テスター指定を直接変更する。
  // カード決済を通している会社は、決済側からの通知で billingStatus が上書きされる。
  const savePlan = async (c, patch) => {
    setBusy(true);
    try {
      await updateDoc(doc(db, "companies", c.id), { ...patch, updatedAt: Timestamp.now() });
      await fetchCompanies();
      setEditing(null);
    } catch (e) {
      console.error("プランの変更に失敗しました:", e);
      alert("変更に失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  const toggleTester = async (c) => {
    const next = !c.isTester;
    if (next && !window.confirm(
      `${c.name} をテスターに設定します。\n\n` +
      "テスターの会社は、支払いの有無にかかわらず有料プランの機能（ライブラリー・給与計算）を使えるようになります。"
    )) return;
    await savePlan(c, { isTester: next });
  };

  const codeMatches = !!deleting &&
    confirmCode.trim().toUpperCase() === String(deleting.id).toUpperCase();
  const dormantCount = companies.filter(isDormant).length;
  const shown = onlyDormant ? companies.filter(isDormant) : companies;

  return (
    <div style={st.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2 style={st.title}>🏢 会社管理</h2>
        <button style={st.addBtn} onClick={() => setShowForm(!showForm)}>{showForm ? "キャンセル" : "＋ 会社を追加"}</button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={st.form}>
          <h3 style={{ color: "#2c3e50", marginBottom: "16px" }}>新規会社登録</h3>
          <label style={st.label}>会社名</label>
          <input style={st.input} type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="例：株式会社サンプル" />
          <label style={st.label}>管理者メールアドレス</label>
          <input style={st.input} type="email" value={form.adminEmail} onChange={e => setForm({ ...form, adminEmail: e.target.value })} required />
          <label style={st.label}>プラン</label>
          <select style={st.input} value={form.plan} onChange={e => {
            const planMaxUsers = { free: 3, small: 20, standard: 50 };
            setForm({ ...form, plan: e.target.value, maxUsers: planMaxUsers[e.target.value] });
          }}>
            <option value="free">フリー（無料・〜3名）</option>
            <option value="small">スモール（500円/月・〜20名）</option>
            <option value="standard">スタンダード（1,500円/月・〜50名）</option>
          </select>
          <label style={st.label}>最大ユーザー数</label>
          <input style={st.input} type="number" value={form.maxUsers} onChange={e => setForm({ ...form, maxUsers: e.target.value })} />
          <button style={st.submitBtn} type="submit" disabled={submitting}>{submitting ? "登録中..." : "登録する"}</button>
        </form>
      )}

      {/* 申込フォームURL */}
      <div style={{ backgroundColor: "#fff", padding: "16px 20px", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "13px", color: "#7f8c8d" }}>📋 申込フォームURL：</span>
        <code style={{ fontSize: "13px", backgroundColor: "#f0f4f8", padding: "4px 10px", borderRadius: "6px", color: "#2c3e50" }}>{SIGNUP_URL}</code>
        <button style={{ padding: "6px 14px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
          onClick={() => { navigator.clipboard.writeText(SIGNUP_URL); alert("コピーしました"); }}>
          コピー
        </button>
      </div>

      <div style={{ marginBottom: "8px", color: "#7f8c8d", fontSize: "14px" }}>契約会社数：{companies.length}社</div>

      {dormantCount > 0 && (
        <div style={st.dormantBar}>
          <span>⚠️ 6か月以上ログインのない会社が <strong>{dormantCount}社</strong> あります。</span>
          <button style={st.filterBtn} onClick={() => setOnlyDormant(!onlyDormant)}>
            {onlyDormant ? "すべて表示" : "該当だけ表示"}
          </button>
        </div>
      )}

      {shown.map(c => (
        <div key={c.id} style={st.card}>
          <div style={st.cardLeft}>
            {/* ロゴ表示・アップロード */}
            <div style={st.logoArea}>
              {c.logo
                ? <img src={c.logo} alt="ロゴ" style={st.logoImg} />
                : <div style={st.logoPlaceholder}>🏢</div>
              }
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={st.logoUploadBtn}>
                  {uploadingLogoFor === c.id ? "アップロード中..." : "ロゴを変更"}
                  <input type="file" accept="image/*" style={{ display: "none" }}
                    onChange={e => handleLogoUpload(c.id, e.target.files[0])}
                    disabled={uploadingLogoFor === c.id}
                  />
                </label>
                {c.logo && (
                  <button style={st.logoDeleteBtn} onClick={() => handleLogoDelete(c.id)}>削除</button>
                )}
              </div>
            </div>

            <div style={st.companyName}>{c.name}</div>
            <div style={st.sub}>会社コード：<code style={st.code}>{c.id}</code>
              <button style={{ marginLeft: "8px", padding: "2px 8px", fontSize: "11px", cursor: "pointer", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff" }}
                onClick={() => { navigator.clipboard.writeText(c.id); alert("コピーしました"); }}>コピー</button>
            </div>
            <div style={st.sub}>管理者：{c.adminEmail || c.contactName || "—"}</div>
            <div style={st.sub}>
              プラン：{PLAN_LABELS[c.plan] || c.plan}{c.maxUsers ? ` / 最大${c.maxUsers}名` : ""}
              <button style={st.planBtn} onClick={() => setEditing({
                id: c.id, name: c.name,
                plan: c.plan || "free",
                billingStatus: c.billingStatus || "trial",
                maxUsers: c.maxUsers || 5,
                isTester: !!c.isTester,
              })}>変更</button>
            </div>
            {c.billingStatus && (() => {
              const bl = BILLING_LABELS[c.billingStatus] || { text: c.billingStatus, color: "#999" };
              return <div style={st.sub}>決済状況：<span style={{ color: bl.color, fontWeight: "bold" }}>{bl.text}</span></div>;
            })()}
            {c.isTester && <div style={st.testerRow}><span style={st.testerBadge}>テスター</span>支払いなしで有料機能を利用できます</div>}
            <div style={st.sub}>登録日時：{fmtDT(c.createdAt)}</div>
            <div style={{ ...st.sub, ...(isDormant(c) ? st.dormantText : {}) }}>
              最終ログイン：{lastLogins[c.id]
                ? `${lastLogins[c.id].toLocaleString("ja-JP")}（${daysSince(lastLogins[c.id])}日前）`
                : "記録なし"}
            </div>
            {isDormant(c) && (
              <div style={st.dormant}>
                ⚠️ 6か月以上ログインがありません。削除の検討対象です。
              </div>
            )}
          </div>
          <div style={st.cardRight}>
            <span style={{ ...st.statusBadge, backgroundColor: c.status === "active" ? "#d5f5e3" : "#fde8e8", color: c.status === "active" ? "#27ae60" : "#e74c3c" }}>
              {c.status === "active" ? "稼働中" : "停止中"}
            </span>
            <button style={{ ...st.toggleBtn, backgroundColor: c.status === "active" ? "#e74c3c" : "#27ae60" }} onClick={() => toggleStatus(c)}>
              {c.status === "active" ? "停止" : "再開"}
            </button>
            <button style={{ ...st.testerBtn, ...(c.isTester ? st.testerBtnOn : {}) }}
              disabled={busy} onClick={() => toggleTester(c)}>
              {c.isTester ? "テスター解除" : "テスターにする"}
            </button>
            <button style={st.deleteCompanyBtn} disabled={busy} onClick={() => openDelete(c)}>
              会社を削除
            </button>
          </div>
        </div>
      ))}

      {companies.length === 0 && <p style={st.empty}>登録された会社はありません</p>}

      {deleting && (
        <div style={st.overlay}>
          <div style={st.modal}>
            <h3 style={{ ...st.modalTitle, color: "#c0392b" }}>会社を削除します</h3>
            <p style={st.delLead}>
              <strong>{deleting.name}</strong>（{deleting.id}）と、この会社に紐づくデータをすべて削除します。
              <strong style={{ color: "#c0392b" }}>元に戻せません。</strong>
            </p>

            <div style={st.delCounts}>
              {preview === null ? <span style={{ color: "#999" }}>件数を確認しています...</span>
                : Object.entries(preview).filter(([, n]) => n > 0).length === 0
                  ? <span style={{ color: "#999" }}>紐づくデータはありません</span>
                  : Object.entries(preview).filter(([, n]) => n > 0).map(([k, n]) => (
                    <span key={k} style={st.delCount}>{LABELS[k] || k} {n}件</span>
                  ))}
            </div>

            <p style={st.delWarn}>
              賃金台帳・出勤簿には法律上の保存義務（3年）があります。必要なデータは、
              削除する前に賃金台帳と勤怠CSVで書き出しておいてください。
            </p>
            <p style={st.delNote}>
              社員のログイン用アカウントも一緒に削除します。会社データだけを消すと、
              該当の方はアプリを使えないうえに、同じメールアドレスで登録し直せなくなるためです。
              運営（ご自身）のアカウントは削除されません。
            </p>

            <label style={st.mLabel}>確認のため、会社コード「{deleting.id}」を入力してください</label>
            <input style={{ ...st.mInput, width: "100%" }} value={confirmCode}
              onChange={e => setConfirmCode(e.target.value)} placeholder={deleting.id} />

            <div style={st.modalBtns}>
              <button style={st.delBtn} disabled={busy || !codeMatches}
                onClick={doDelete}>{busy ? "削除中..." : "完全に削除する"}</button>
              <button style={st.cancelBtn} onClick={() => setDeleting(null)} disabled={busy}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div style={st.overlay}>
          <div style={st.modal}>
            <h3 style={st.modalTitle}>プランの変更：{editing.name}</h3>

            <label style={st.mLabel}>プラン</label>
            <div style={st.chipRow}>
              {Object.entries(PLAN_LABELS).map(([k, v]) => (
                <button key={k} type="button"
                  style={{ ...st.chip, ...(editing.plan === k ? st.chipOn : {}) }}
                  onClick={() => setEditing({ ...editing, plan: k })}>{v}</button>
              ))}
            </div>

            <label style={st.mLabel}>決済状況</label>
            <div style={st.chipRow}>
              {Object.entries(BILLING_LABELS).map(([k, v]) => (
                <button key={k} type="button"
                  style={{ ...st.chip, ...(editing.billingStatus === k ? st.chipOn : {}) }}
                  onClick={() => setEditing({ ...editing, billingStatus: k })}>{v.text}</button>
              ))}
            </div>
            <p style={st.mNote}>
              「決済中」にすると有料プランの機能が使えるようになります。
              カード決済を通している会社は、決済側からの通知でこの値が上書きされます。
              支払いを伴わない開放は「テスター」の方が適しています。
            </p>

            <label style={st.mLabel}>最大人数</label>
            <input style={st.mInput} type="number" min="1" value={editing.maxUsers}
              onChange={e => setEditing({ ...editing, maxUsers: Number(e.target.value || 1) })} />

            <div style={st.modalBtns}>
              <button style={st.saveBtn} disabled={busy}
                onClick={() => savePlan(editing, {
                  plan: editing.plan,
                  billingStatus: editing.billingStatus,
                  maxUsers: Number(editing.maxUsers) || 1,
                })}>保存する</button>
              <button style={st.cancelBtn} onClick={() => setEditing(null)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "900px", margin: "0 auto" },
  planBtn: { marginLeft: "8px", padding: "2px 10px", fontSize: "11px", cursor: "pointer", border: "1px solid #2980b9", color: "#2980b9", borderRadius: "4px", backgroundColor: "#fff" },
  dormantText: { color: "#c0392b", fontWeight: "bold" },
  dormant: { fontSize: "12px", color: "#c0392b", backgroundColor: "#fdeaea", borderRadius: "6px", padding: "5px 10px", marginTop: "4px", display: "inline-block" },
  dormantBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", backgroundColor: "#fdeaea", border: "1px solid #f5b7b1", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", fontSize: "13px", color: "#922b21" },
  filterBtn: { padding: "6px 14px", backgroundColor: "#fff", color: "#c0392b", border: "1px solid #c0392b", borderRadius: "8px", cursor: "pointer", fontSize: "12px" },
  deleteCompanyBtn: { marginTop: "6px", padding: "6px 12px", backgroundColor: "#fff", color: "#c0392b", border: "1px solid #c0392b", borderRadius: "8px", cursor: "pointer", fontSize: "12px" },
  delLead: { fontSize: "14px", color: "#2c3e50", lineHeight: "1.8", marginTop: 0 },
  delCounts: { display: "flex", gap: "6px", flexWrap: "wrap", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px" },
  delCount: { fontSize: "12px", color: "#2c3e50", backgroundColor: "#fff", border: "1px solid #e6ebf0", borderRadius: "6px", padding: "3px 9px" },
  delWarn: { fontSize: "12px", color: "#8a5a00", backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "8px", padding: "10px 12px", lineHeight: "1.8" },
  delNote: { fontSize: "12px", color: "#7f8c8d", lineHeight: "1.8" },
  delBtn: { padding: "10px 28px", backgroundColor: "#c0392b", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  testerRow: { fontSize: "12px", color: "#8a5a00", marginTop: "4px" },
  testerBadge: { backgroundColor: "#f39c12", color: "#fff", borderRadius: "10px", padding: "2px 10px", fontSize: "11px", fontWeight: "bold", marginRight: "8px" },
  testerBtn: { marginTop: "6px", padding: "6px 12px", backgroundColor: "#fff", color: "#e67e22", border: "1px solid #e67e22", borderRadius: "8px", cursor: "pointer", fontSize: "12px" },
  testerBtnOn: { backgroundColor: "#e67e22", color: "#fff" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "50px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "500px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 16px", fontSize: "18px" },
  mLabel: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginTop: "14px", marginBottom: "6px" },
  mInput: { width: "120px", padding: "9px 11px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  mNote: { fontSize: "12px", color: "#7f8c8d", lineHeight: "1.8", marginTop: "8px", marginBottom: 0 },
  chipRow: { display: "flex", gap: "6px", flexWrap: "wrap" },
  chip: { padding: "7px 14px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  chipOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  modalBtns: { display: "flex", gap: "12px", marginTop: "22px" },
  saveBtn: { padding: "10px 28px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  cancelBtn: { padding: "10px 24px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  title: { color: "#2c3e50", margin: 0 },
  addBtn: { padding: "10px 20px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "24px" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  submitBtn: { padding: "12px 32px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  cardLeft: { flex: 1 },
  cardRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "12px" },
  logoArea: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "14px" },
  logoImg: { width: "64px", height: "64px", objectFit: "contain", borderRadius: "8px", border: "1px solid #eee", backgroundColor: "#f8f8f8" },
  logoPlaceholder: { width: "64px", height: "64px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", backgroundColor: "#f0f4f8", borderRadius: "8px", border: "1px solid #eee" },
  logoUploadBtn: { padding: "6px 14px", backgroundColor: "#2c3e50", color: "#fff", borderRadius: "6px", fontSize: "12px", cursor: "pointer", textAlign: "center" },
  logoDeleteBtn: { padding: "5px 14px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", fontSize: "12px", cursor: "pointer" },
  companyName: { fontSize: "18px", fontWeight: "bold", color: "#2c3e50", marginBottom: "8px" },
  sub: { fontSize: "13px", color: "#7f8c8d", marginBottom: "2px" },
  code: { backgroundColor: "#f0f4f8", padding: "2px 6px", borderRadius: "4px", fontSize: "12px" },
  statusBadge: { padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold" },
  toggleBtn: { padding: "8px 20px", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
};

export default CompanyManagement;
